import styled, {css, keyframes} from 'styled-components';
import {peacock} from './index.style';

export const ICON_COLORS: Record<string, string> = {
  route: '#8A8470',
  graph: '#4F86A8',
  code: '#A8763E',
  db: '#6B5B95',
  shield: '#5C7A4A',
  rca: '#B5483B',
  forecast: '#2F7B6E',
  whatif: '#8B6FB0',
  spark: peacock.primary,
};

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const TraceWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: clamp(0.375rem, 0.4vw, 0.625rem);
`;

export const TraceStepRow = styled.div<{$icon: string}>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
  animation: ${fadeInUp} 0.35s ease both;
`;

export const StepIcon = styled.div<{$icon: string}>`
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-top: 1px;
  background: ${({$icon}) => ICON_COLORS[$icon] || peacock.primary};

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const StepBody = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 1px;
`;

export const StepLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.75rem, 0.35vw + 0.65rem, 0.8125rem);
  color: #2d2d2d;
  font-weight: 500;
`;

export const StepSub = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.6875rem, 0.3vw + 0.55rem, 0.75rem);
  color: #9b9686;
  margin-top: 1px;
`;

export const StepStatus = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.6875rem, 0.3vw + 0.55rem, 0.75rem);
  color: #9b9686;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const Spinner = styled.div`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 2px solid #e5e2d9;
  border-top-color: #9b9686;
  animation: ${spin} 0.7s linear infinite;
`;

export const CheckIcon = styled.span`
  color: #5c7a4a;
  display: flex;

  svg {
    width: 11px;
    height: 11px;
  }
`;

export const FailIcon = styled.span`
  color: #b5483b;
  display: flex;

  svg {
    width: 11px;
    height: 11px;
  }
`;

export const FailMeta = styled.span`
  color: #b5483b;
`;

export const NestedGroup = styled.div`
  margin-left: 30px;
  border-left: 2px solid #e5e2d9;
  padding-left: 14px;
  margin-bottom: 10px;
`;

export const GroupLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9b9686;
  margin-bottom: 8px;
  font-weight: 600;
`;

export const SourcesRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #9b9686;
  margin: 2px 0 clamp(0.75rem, 0.7vw, 1.125rem) 0;
  animation: ${fadeInUp} 0.35s ease both;
`;

export const SourcesLabel = styled.span`
  font-weight: 600;
  color: #6b6655;
  margin-right: 2px;
`;

export const SourcePill = styled.span`
  background: #f4f2ea;
  border: 1px solid #e5e2d9;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11.5px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  color: #6b6655;
`;

export const ChartRevealWrap = styled.div`
  animation: ${fadeInUp} 0.4s ease both;
`;

export const StreamHeader = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  font-weight: 600;
  color: #2d2d2d;
  margin: clamp(0.625rem, 0.5vw, 0.875rem) 0 6px 0;
`;

export const StreamPara = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  line-height: 1.55;
  color: #2d2d2d;
  margin: 0 0 clamp(0.5rem, 0.5vw, 0.75rem) 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

const caretBlink = keyframes`
  50% { opacity: 0; }
`;

export const Caret = styled.span<{$show: boolean}>`
  display: ${({$show}) => ($show ? 'inline-block' : 'none')};
  width: 2px;
  height: 1em;
  margin-left: 2px;
  background-color: #2d2d2d;
  vertical-align: text-bottom;
  animation: ${caretBlink} 1s steps(2, start) infinite;
`;

export const StreamTableWrap = styled.div`
  border: 1px solid rgba(0, 90, 139, 0.14);
  border-radius: 10px;
  overflow: hidden;
  margin: 4px 0 clamp(0.5rem, 0.5vw, 0.75rem) 0;
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.75rem, 0.35vw + 0.65rem, 0.8438rem);
`;

export const StreamTableRow = styled.div<{$visible: boolean; $isHeader?: boolean}>`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  background: ${({$isHeader}) => ($isHeader ? '#fafcfe' : 'transparent')};
  font-weight: ${({$isHeader}) => ($isHeader ? 700 : 400)};
  color: ${({$isHeader}) => ($isHeader ? '#005A8B' : '#2d2d2d')};
  border-bottom: 1px solid rgba(0, 90, 139, 0.1);
  opacity: ${({$visible}) => ($visible ? 1 : 0)};
  transform: translateY(${({$visible}) => ($visible ? '0' : '4px')});
  transition: opacity 0.3s ease, transform 0.3s ease;

  &:last-child {
    border-bottom: none;
  }
`;

export const ToneValue = styled.span<{$tone?: 'pos' | 'neg'}>`
  ${({$tone}) =>
    $tone === 'neg'
      ? css`
          color: #b5483b;
          font-weight: 600;
        `
      : $tone === 'pos'
        ? css`
            color: #5c7a4a;
            font-weight: 600;
          `
        : ''}
`;

export const QueriesPanel = styled.div<{$visible: boolean}>`
  margin-top: 16px;
  border-top: 1px solid #e5e2d9;
  padding-top: 14px;
  opacity: ${({$visible}) => ($visible ? 1 : 0)};
  transform: translateY(${({$visible}) => ($visible ? '0' : '6px')});
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

export const QueriesToggle = styled.button<{$open: boolean}>`
  display: flex;
  align-items: center;
  gap: 7px;
  background: none;
  border: none;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #6b6655;
  cursor: pointer;
  padding: 4px 0;

  svg {
    width: 10px;
    height: 10px;
    transition: transform 0.25s ease;
    transform: rotate(${({$open}) => ($open ? '90deg' : '0deg')});
  }
`;

export const QueriesBody = styled.div<{$open: boolean}>`
  max-height: ${({$open}) => ($open ? '3600px' : '0')};
  overflow: hidden;
  transition: max-height 0.45s ease;
  margin-top: 2px;
`;

export const QueryBlockWrap = styled.div`
  margin-top: 14px;
`;

export const QueryHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #6b6655;
  margin-bottom: 6px;
`;

export const QueryDot = styled.span<{$color: string}>`
  width: 9px;
  height: 9px;
  border-radius: 3px;
  display: inline-block;
  flex-shrink: 0;
  background: ${({$color}) => $color};
`;

export const CodeBlockPre = styled.pre`
  background: #f4f2ea;
  border: 1px solid #e5e2d9;
  border-radius: 8px;
  padding: 12px 14px;
  margin: 0;
  overflow-x: auto;

  code {
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.65;
    color: #2d2d2d;
    white-space: pre;
  }
`;

export const EndSourcesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
`;
